import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import {
  IBudgetTagItem,
  IBudgetTagTemplate,
  IGlAccountLabel,
} from '../../../../../store/settings/settings.interface';
import { Store } from '@ngrx/store';
import { getSelectedBudgetTagTemplate } from '../../../../../store/settings/settings.selectors';
import { takeUntil } from 'rxjs/operators';
import { settingsActions } from '../../../../../store/settings/settings.actions';
import { FormsModule } from '@angular/forms';
import { NgScrollbar } from 'ngx-scrollbar';
import { TemplatesHelpComponent } from '../templates-help/templates-help.component';
import { NgIf, NgFor, NgClass } from '@angular/common';
import { TemplateSelectorComponent } from '../template-selector/template-selector.component';

@Component({
  selector: 'app-budget-tag-templates',
  templateUrl: './budget-tag-templates.component.html',
  styleUrls: ['./budget-tag-templates.component.scss'],
  standalone: true,
  imports: [
    TemplateSelectorComponent,
    NgIf,
    TemplatesHelpComponent,
    NgScrollbar,
    NgFor,
    NgClass,
    FormsModule,
  ],
})
export class BudgetTagTemplatesComponent implements OnInit, OnDestroy {
  isDestroyed$ = new Subject<void>();
  selectedTemplate: IBudgetTagTemplate;
  newItemValue = '';
  showHelp = false;

  constructor(private store: Store) {}

  ngOnInit(): void {
    this.store
      .select(getSelectedBudgetTagTemplate)
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((template) => {
        if (template) {
          this.selectedTemplate = {
            ...template,
            tags: template.tags.filter((tag) => !tag.is_deleted),
          };
        }
      });
  }

  onInputChange(item: IBudgetTagItem, index: number, event) {
    const element = event.target as HTMLInputElement;
    const newItem: IGlAccountLabel = { ...item, name: element.value };
    this.store.dispatch(settingsActions.updateBudgetTagTemplateItem({ newItem }));
  }

  onNewItemChange() {
    this.store.dispatch(
      settingsActions.addBudgetTagTemplateItem({ newItemName: this.newItemValue }),
    );
    this.newItemValue = '';
  }

  removeItem(item: IGlAccountLabel) {
    this.store.dispatch(settingsActions.removeBudgetTagTemplateItem({ itemId: item.id }));
  }

  onPasteEvent(fromIndex: number, event: ClipboardEvent) {
    const textToPaste = event.clipboardData.getData('text');
    const itemNames = textToPaste.split('\n');
    if (itemNames.length <= 1) {
      return;
    }
    // if more than 1 row is going to be inserted, overwrite the old values with the new ones
    event.preventDefault();
    if (!fromIndex && fromIndex !== 0) {
      // if items are inserted to the end of list (empty input), just create them
      for (const name of itemNames) {
        this.store.dispatch(settingsActions.addBudgetTemplateItem({ newItemName: name }));
      }
    } else {
      // if items are inserted in the middle of the items,
      // overwrite from `fromIndex` to the end of existing items
      // and then insert new ones if there is any
      let index = fromIndex;
      for (const name of itemNames) {
        const id = this.selectedTemplate.tags?.[index]?.id;
        index++;
        if (id) {
          const newItem: IGlAccountLabel = { id, name };
          this.store.dispatch(settingsActions.updateBudgetTagTemplateItem({ newItem }));
        } else {
          this.store.dispatch(settingsActions.addBudgetTemplateItem({ newItemName: name }));
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.isDestroyed$.next();
    this.isDestroyed$.complete();
  }

  onDiscard() {
    history.back();
  }

  onSave() {
    this.store.dispatch(settingsActions.saveBudgetTagTemplate());
  }

  unselectTemplate() {
    this.selectedTemplate = null;
  }
}
